/* eslint-disable react/destructuring-assignment */
/**
 * This is the Redux actions to work on the Groupings
 */
import { GlobalState } from '@leverege/ui-redux';
import ArrayUtil from '@leverege/array-util';
import AggRequestorFactory from "./AggRequestorFactory.js";
import GroupingsReducer from "./GroupingsReducer.js";
import Constants from "./Constants.js";
/**
 * Sets grouping data for the given id. This does not set the currently
 * selected groupings object. Pass the result of this into redux's dispatch.
 * This will set the data for the non-current grouping, allowing you
 * to manage/ignore the currentId as desired 
 */
function setGrouping(objRef, grouping) {
  if (objRef != null && typeof objRef === 'object' && objRef.id !== null) {
    return {
      type: Constants.SET_GROUPING_DATA,
      id: objRef.id,
      objRef,
      grouping
    };
  }
  throw Error('Groupings.setGroupings: objRef must be an object { id, type, ref }');
}

/**
 * Sets the currently active grouping. If an objRef and grouping is supplied, it
 * will also set the grouping data. Pass the result of this into redux's dispatch.
 */
function setCurrentGrouping(objRefOrId, grouping) {
  return {
    type: Constants.SET_CURRENT_GROUPING,
    id: objRefOrId,
    grouping
  };
}

/**
 * Sets the grouping's section value. If objRefOrId is null, the current grouping
 * id will be used. Pass the result of this into redux's dispatch.
 */
function setSelectedSectionValue(sectionId, value, objRefOrId) {
  return {
    type: Constants.SET_SECTION_VALUE,
    sectionId,
    value,
    id: objRefOrId
  };
}

/**
 * Sets the currently active grouping's section value. 
 * Pass the result of this into redux's dispatch.
 */
function setSelectedSectionValues(values, objRefOrId) {
  return {
    type: Constants.SET_SECTION_VALUES,
    values,
    id: objRefOrId
  };
}

/**
 * Sets the groupings' agg values. 
 * Pass the result of this into redux's dispatch.
 */
function setGroupingAgg(objRefOrId, aggName, agg) {
  return {
    type: Constants.SET_GROUPING_AGG,
    id: objRefOrId,
    agg,
    aggName
  };
}

/**
 * Sets the groupings' agg status value. 
 * Pass the result of this into redux's dispatch.
 */
function setGroupingAggStatus(objRefOrId, aggName, status, error = null) {
  return {
    type: Constants.SET_GROUPING_AGG_STATUS,
    id: objRefOrId,
    aggName,
    status,
    error
  };
}
function requestGroupingAgg(objRefOrId, aggName, context) {
  return async (dispatch, getState, opts) => {
    dispatch(setGroupingAggStatus(objRefOrId, aggName, Constants.AGG_LOADING));
    const {
      objRef,
      grouping
    } = GroupingsReducer.getGroupingAndObjRef(getState(), objRefOrId);
    if (grouping && objRef) {
      const {
        aggRequestorType
      } = grouping;
      if (aggRequestorType) {
        const requestor = AggRequestorFactory.get(aggRequestorType);
        if (requestor) {
          try {
            const aggs = await requestor.requestAggs(objRef, grouping, context, {
              dispatch,
              getState,
              opts
            });
            if (context.changeSelection) {
              reselectSection(aggs, objRef, grouping);
            }
            return dispatch(setGroupingAgg(objRefOrId, aggName, aggs));
          } catch (error) {
            dispatch(setGroupingAggStatus(objRefOrId, aggName, Constants.AGG_ERROR, error));
            throw error;
          }
        }
        return null;
      }
    }
    throw Error('Grouping does not exist for', objRefOrId);
  };
}
function reselectSection(aggs, objRef, grouping) {
  // Check to see if the current selection for the sections have zero items, and switch the selected values
  let values = GroupingsReducer.getSelectedSectionValues(GlobalState.get(), objRef);
  values = values == null ? {} : {
    ...values
  };
  let changed = false;
  Object.keys(values).forEach(sectionId => {
    const sectionValue = values[sectionId];
    const sAggs = aggs[sectionId];
    if (sAggs[sectionValue] === 0 || sAggs[sectionValue] == null) {
      let all, missing, val;
      const section = getSectionData(grouping, sectionId);
      const sValues = section?.values || [];
      // Walk the groupings so we get missing items in order
      sValues.forEach(sVal => {
        const k = sVal.value;
        if (sAggs[k] == null || sAggs[k] <= 0) {
          return;
        }
        if (k === Constants.ALL_SECTION_VALUE) {
          all = sAggs[k];
        } else if (k.startsWith(Constants.MISSING_SECTION_VALUE)) {
          if (missing == null || sAggs[k] > sAggs[missing]) {
            missing = k;
          }
        } else if (val == null || sAggs[k] > sAggs[val]) {
          val = k;
        }
      });
      if (val || missing || all) {
        changed = true;
        values[sectionId] = val || missing || all;
      }
    }
  });
  if (changed) {
    GlobalState.dispatch(setSelectedSectionValues(values, objRef));
  }
}
const isMissing = v => {
  return v && v.startsWith(Constants.MISSING_SECTION_VALUE);
};
const isAll = v => {
  return v === Constants.ALL_SECTION_VALUE;
};

/**
 * Given a grouping object and a section id this will return the
 * section data. Note This is not a State method, but a group object access method.
 */
function getSectionData(grp, sectionId) {
  if (grp == null || sectionId == null || grp.sections == null) {
    return null;
  }
  const sections = grp.sections;
  for (let n = 0; n < sections.length; n++) {
    if (sections[n].id === sectionId) {
      return sections[n];
    }
  }
  return null;
}

/**
 * Given a grouping object, a section id, and value, this will return the
 * value. Note This is not a State method, but a group object access method.
 */
const getValueData = (grp, sectionId, value) => {
  if (grp == null || sectionId == null || value == null || grp.sections == null) {
    return null;
  }
  const section = typeof sectionId === 'object' ? sectionId : ArrayUtil.find(grp.sections, 'id', sectionId);
  if (section != null && section.values != null) {
    return ArrayUtil.find(section.values, 'value', value);
  }
  return null;
};
function areSelectionsEqual(av, bv) {
  if (av === bv) {
    return true;
  }
  if (av == null) {
    return bv == null || Object.keys(bv).length === 0;
  }
  if (bv == null) {
    return av == null || Object.keys(av).length === 0;
  }
  const ak = Object.keys(av);
  const bk = Object.keys(bv);
  if (ak.length !== bk.length) {
    return false;
  }
  for (let n = 0; n < ak.length; n++) {
    const k = ak[n];
    if (av[k] !== bv[k]) {
      return false;
    }
  }
  return true;
}

/**
 * Returns true if grouping is null or all values in all
 * sections are length zero or null
 * @param {object} grouping the grouping object
 */
function isGroupingEmpty(grouping) {
  if (grouping == null || grouping.sections == null || grouping.sections.length === 0) {
    return true;
  }
  return grouping.sections.every(s => {
    return s.values == null || s.values.length === 0;
  });
}
export default {
  setGrouping,
  setCurrentGrouping,
  setSelectedSectionValue,
  setGroupingAgg,
  setSelectedSectionValues,
  setGroupingAggStatus,
  requestGroupingAgg,
  getObjRef: GroupingsReducer.getObjRef,
  getGrouping: GroupingsReducer.getGrouping,
  getGroupingAndObjRef: GroupingsReducer.getGroupingAndObjRef,
  getCurrentGroupingId: GroupingsReducer.getCurrentGroupingId,
  getSelectedSection: GroupingsReducer.getSelectedSection,
  getSelectedSectionValue: GroupingsReducer.getSelectedSectionValue,
  getSelectedSectionValues: GroupingsReducer.getSelectedSectionValues,
  getGroupingAgg: GroupingsReducer.getGroupingAgg,
  getGroupingAggTime: GroupingsReducer.getGroupingAggTime,
  getGroupingAggStatus: GroupingsReducer.getGroupingAggStatus,
  addUpdateListener: GroupingsReducer.addUpdateListener,
  removeUpdateListener: GroupingsReducer.removeUpdateListener,
  ALL_SECTION_VALUE: Constants.ALL_SECTION_VALUE,
  MISSING_SECTION_VALUE: Constants.MISSING_SECTION_VALUE,
  isMissing,
  isAll,
  getSectionData,
  getValueData,
  areSelectionsEqual,
  isGroupingEmpty
};